import { type TwoSideQuestionBlock, QuestionType, type QnA_Block, type RankQuestionBlock } from "@/q-n-a.ts";
import { Box, Radio, Group, Text, Stack, Button, Divider, Paper, SegmentedControl, Image } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useListState } from "@mantine/hooks";
import { groupQuestionsWithImages, isValidUrl } from "@/question.grouping";
import RankQuestion from "../componets/rank.question.tsx";

export default function TwoSidedPrompts(props: {
    questions: QnA_Block[],
    onSubmit: (answers: QnA_Block[]) => void,
    onProgress: (qid: string | number) => void
}) {
    props.questions.forEach(q => {
        if (!q.answerLog) q.answerLog = {};
    });

    // Separate questions by type
    const twoSidedQuestions = props.questions.filter((q): q is TwoSideQuestionBlock =>
        'left' in q && 'right' in q && q.qType !== QuestionType.RANK
    );
    const rankQuestions = props.questions.filter((q): q is RankQuestionBlock =>
        q.qType === QuestionType.RANK || 'question' in q
    );

    const [twoSidedAnswers, twoSidedHandlers] = useListState<TwoSideQuestionBlock>(twoSidedQuestions);
    const [rankAnswers, rankHandlers] = useListState<RankQuestionBlock>(rankQuestions);

    // Combine all answers for grouping and submission
    const allAnswers = useMemo(() => {
        return [...twoSidedAnswers, ...rankAnswers] as QnA_Block[];
    }, [twoSidedAnswers, rankAnswers]);

    const [currentGroup, setCurrentGroup] = useState(0);
    const [manuallyNavigated, setManuallyNavigated] = useState(false);

    const QUESTIONS_PER_GROUP = 5;

    // Use the new grouping logic
    const questionGroups = useMemo(() => {
        return groupQuestionsWithImages(allAnswers, QUESTIONS_PER_GROUP);
    }, [allAnswers]);

    const totalGroups = questionGroups.length;

    // Get current group of questions
    const getCurrentQuestions = () => {
        return questionGroups[currentGroup] || [];
    };

    const handleContinue = () => {
        if (currentGroup < totalGroups - 1) {
            setCurrentGroup(prev => prev + 1);
            setManuallyNavigated(false); // Reset manual navigation flag
        } else {
            props.onSubmit(allAnswers);
        }
    };

    const handleBack = () => {
        if (currentGroup > 0) {
            setCurrentGroup(prev => prev - 1);
            setManuallyNavigated(true); // Mark as manually navigated
        }
    };

    // Check if all questions in current group are answered
    const isCurrentGroupComplete = () => {
        const currentQuestions = getCurrentQuestions();
        return currentQuestions.every((q: QnA_Block) => {
            if (q.qType === QuestionType.RANK || 'question' in q) {
                return (q as RankQuestionBlock).answer !== undefined;
            }
            return (q as TwoSideQuestionBlock).answer !== undefined;
        });
    };

    // Handle rank question updates
    const handleRankQuestionChange = (updatedQuestion: RankQuestionBlock) => {
        rankHandlers.applyWhere(
            q => q.qid === updatedQuestion.qid,
            () => updatedQuestion
        );
    };

    // Auto-advance when current group becomes complete (only if not manually navigated)
    useEffect(() => {
        if (isCurrentGroupComplete() && currentGroup < totalGroups - 1 && !manuallyNavigated) {
            const timer = setTimeout(() => {
                handleContinue();
            }, 500); // Small delay to allow user to see the completion
            return () => clearTimeout(timer);
        }
    }, [allAnswers, currentGroup, totalGroups, manuallyNavigated])

    return (
        <Box>
            <Stack gap="md">
                {getCurrentQuestions().map((question: QnA_Block) => {
                    // Check if it's a ranking question
                    if (question.qType === QuestionType.RANK || 'question' in question) {
                        const rankQuestion = question as RankQuestionBlock;
                        return (
                            <RankQuestion
                                key={question.qid}
                                question={rankQuestion}
                                onChange={handleRankQuestionChange}
                                onProgress={props.onProgress}
                            />
                        );
                    }

                    // Otherwise, it's a two-sided question
                    const twoSidedQuestion = question as TwoSideQuestionBlock;
                    let ansValArray = [-2, -1, 0, 1, 2];
                    switch (twoSidedQuestion.qType) {
                        case QuestionType.EI:
                        case QuestionType.NS:
                        case QuestionType.TF:
                        case QuestionType.PJ:
                            ansValArray = [2, 1, 0, -1, -2];
                            break;
                        default:
                            // Default is already correct
                            break;
                    }
                    ansValArray = [-99, ...ansValArray];
                    return (
                        <Paper
                            key={twoSidedQuestion.qid}
                            p="sm"
                            withBorder
                            radius={'24px'}
                        // shadow="md"
                        >
                            <Stack gap={4}>
                                <Group justify="space-between" align="center" wrap="nowrap">
                                    <Text size="1.1rem" w="50%" ta="right">
                                        {twoSidedQuestion.left}
                                    </Text>
                                    <Divider orientation="vertical" />
                                    <Text size="1.1rem" w="50%" ta="left">
                                        {twoSidedQuestion.right}
                                    </Text>
                                </Group>
                                <SegmentedControl
                                    fullWidth
                                    value={twoSidedQuestion.answer?.toString()}

                                    color={(() => {
                                        switch (twoSidedQuestion.answer) {
                                            case -2:
                                                return 'var(--mantine-color-yenta-0)';
                                            case -1:
                                                return 'var(--mantine-color-yenta-4)';
                                            case 1:
                                                return 'var(--mantine-color-yenta-6)';
                                            case 2:
                                                return 'var(--mantine-color-yenta-9)';
                                            case 0:
                                            default:
                                                return 'gray';
                                        }
                                    })()}
                                    onChange={(value) => twoSidedHandlers.applyWhere(
                                        q => q.qid === twoSidedQuestion.qid,
                                        q => {
                                            const al = q.answerLog || {};
                                            al[Date.now()] = parseInt(value);
                                            props.onProgress(q.qid);
                                            return ({ ...q, answerLog: al, answer: parseInt(value) as 0 | 2 | 1 | -2 | -1 })
                                        }
                                    )}
                                    data={ansValArray.map((value, dex) => {
                                        switch (value) {
                                            case -2:
                                                return { label: dex === 1 ? '⏮' : '⏭', value: value.toString() };
                                            case -1:
                                                return { label: dex === 2 ? '◀' : '▶', value: value.toString() };
                                            case 1:
                                                return { label: dex === 2 ? '◀' : '▶', value: value.toString() };
                                            case 2:
                                                return { label: dex === 1 ? '⏮' : '⏭', value: value.toString() };
                                            case 0:
                                                return { label: '•', value: value.toString() };
                                            default:
                                                return { label: 'Pick', value: 'NaN', disabled: !!twoSidedQuestion.answer };
                                        }
                                    })}
                                    radius={24}
                                />
                                {(twoSidedQuestion.lUrl || twoSidedQuestion.rUrl) && (
                                    <Group justify="center" gap={0} wrap="nowrap" grow>
                                        {/* {twoSidedQuestion.lUrl && <Image src={twoSidedQuestion.lUrl} alt={twoSidedQuestion.left} h={240} w='auto' fit='contain' mx='auto' maw={260}  />} */}
                                        {twoSidedQuestion.lUrl && <picture>
                                            <source srcSet={`${twoSidedQuestion.lUrl}.avif`} type='image/avif' />
                                            <source srcSet={`${twoSidedQuestion.lUrl}.webp`} type='image/webp' />
                                            <img src={`${twoSidedQuestion.lUrl}.gif`} alt={twoSidedQuestion.left} loading="lazy" height={240} width={'auto'} style={{ margin: 'auto', objectFit: 'contain', maxWidth: 260 }} />
                                        </picture>}
                                        {twoSidedQuestion.rUrl && <picture>
                                            <source srcSet={`${twoSidedQuestion.rUrl}.avif`} type='image/avif' />
                                            <source srcSet={`${twoSidedQuestion.rUrl}.webp`} type='image/webp' />
                                            <img src={`${twoSidedQuestion.rUrl}.gif`} alt={twoSidedQuestion.right} loading="lazy" height={240} width={'auto'} style={{ margin: 'auto', objectFit: 'contain', maxWidth: 260 }} />
                                        </picture>}
                                    </Group>
                                )}
                            </Stack>
                        </Paper>
                    )
                })}
            </Stack>

            <Group justify="center" mt="xl" gap="md">
                {currentGroup > 0 && (
                    <Button
                        onClick={handleBack}
                        variant="light"
                    >
                        Back
                    </Button>
                )}
                <Button
                    onClick={handleContinue}
                    disabled={!isCurrentGroupComplete()}
                >
                    {currentGroup < totalGroups - 1 ? 'Continue' : 'Next'}
                </Button>
            </Group>
        </Box>
    );
}